import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../SupabaseClient";
import { Tooltip } from "react-tooltip";
import {
  InformationCircleIcon,
  PhotoIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";

const sanitizeFileName = (name) =>
  name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-_.]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

const getConstraints = (ratio) => {
  if (ratio === "square")
    return {
      recommended: "400×400px",
      targetW: 400,
      targetH: 400,
      dropClass: "aspect-[1/1] w-40 sm:w-48",
    };
  return {
    recommended: "600×400px",
    targetW: 600,
    targetH: 400,
    dropClass: "aspect-[3/2] w-64 sm:w-80",
  };
};

const getBitmap = async (file) => {
  if (window.createImageBitmap) return await createImageBitmap(file);
  const url = URL.createObjectURL(file);
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject("Impossible de charger l’image.");
    image.src = url;
  });
  URL.revokeObjectURL(url);
  return img;
};

const getCoverCropRect = (srcW, srcH, dstW, dstH) => {
  const srcRatio = srcW / srcH;
  const dstRatio = dstW / dstH;
  if (srcRatio > dstRatio) {
    const sw = Math.round(srcH * dstRatio);
    const sx = Math.round((srcW - sw) / 2);
    return { sx, sy: 0, sw, sh: srcH };
  }
  const sh = Math.round(srcW / dstRatio);
  const sy = Math.round((srcH - sh) / 2);
  return { sx: 0, sy, sw: srcW, sh };
};

const canvasToWebpFile = async (canvas, originalName) => {
  const blob = await new Promise((r) => canvas.toBlob(r, "image/webp", 0.9));
  if (!blob) throw "Impossible de générer l’image.";
  const base =
    sanitizeFileName(originalName.replace(/\.[^.]+$/, "")) || "image";
  return new File([blob], `${base}.webp`, { type: "image/webp" });
};

const getImageSize = (bitmap) => {
  const width = bitmap.naturalWidth || bitmap.width || 0;
  const height = bitmap.naturalHeight || bitmap.height || 0;
  return { width, height };
};

const drawCoverToCanvas = (bitmap, constraints) => {
  const { width, height } = getImageSize(bitmap);
  if (!width || !height) throw "Image invalide.";
  const { sx, sy, sw, sh } = getCoverCropRect(
    width,
    height,
    constraints.targetW,
    constraints.targetH,
  );
  const canvas = document.createElement("canvas");
  canvas.width = constraints.targetW;
  canvas.height = constraints.targetH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    bitmap,
    sx,
    sy,
    sw,
    sh,
    0,
    0,
    constraints.targetW,
    constraints.targetH,
  );
  return canvas;
};

const processImageToTarget = async (file, constraints, allowUpscale) => {
  const bitmap = await getBitmap(file);
  const { width, height } = getImageSize(bitmap);
  if (!width || !height) throw "Image invalide.";
  if (
    !allowUpscale &&
    (width < constraints.targetW || height < constraints.targetH)
  ) {
    throw `Image trop petite (${width}x${height}). Minimum suggéré: ${constraints.recommended}.`;
  }
  const canvas = drawCoverToCanvas(bitmap, constraints);
  return await canvasToWebpFile(canvas, file.name);
};

const uploadToSupabase = async (bucket, file) => {
  const timestamp = new Date().getTime();
  const safe = sanitizeFileName(file.name.replace(/\.[^.]+$/, "")) || "image";
  const path = `${safe}-${timestamp}.webp`;
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, { contentType: file.type });
  if (error) throw error;
  const { data: pub } = supabase.storage.from(bucket).getPublicUrl(data.path);
  return pub.publicUrl;
};

export default function ImageUploader({
  initialImageUrl,
  onTempImageChange,
  bucket = "makeadream-images",
  ratio = "landscape",
  allowUpscale = true,
}) {
  const { t } = useTranslation();
  const constraints = useMemo(() => getConstraints(ratio), [ratio]);
  const tooltipId = useMemo(
    () => `image-info-${Math.random().toString(36).slice(2)}`,
    [],
  );
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState(initialImageUrl);

  useEffect(() => setTempImageUrl(initialImageUrl), [initialImageUrl]);

  const setImage = (url) => {
    setTempImageUrl(url);
    if (onTempImageChange) onTempImageChange(url);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      setError(null);
      const processed = await processImageToTarget(
        file,
        constraints,
        allowUpscale,
      );
      const publicUrl = await uploadToSupabase(bucket, processed);
      setImage(publicUrl);
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err?.message || t("global.uploadFailed");
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = (event) => uploadFile(event.target.files?.[0]);
  const handleDrop = (event) => (
    event.preventDefault(),
    setDragActive(false),
    uploadFile(event.dataTransfer.files?.[0])
  );
  const handleDelete = () => setImage("");

  return (
    <div className="space-y-4">
      {tempImageUrl ? (
        <div className="relative mx-auto" style={{ maxWidth: "320px" }}>
          <img
            src={tempImageUrl}
            alt="Aperçu"
            className="w-full rounded-lg shadow-md object-cover"
          />
          <div className="flex justify-center mt-4">
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-red-600 transition"
            >
              <TrashIcon className="w-4 h-4" />
              Supprimer
            </button>
          </div>
        </div>
      ) : (
        <label
          htmlFor="image-upload"
          onDragOver={(e) => (e.preventDefault(), setDragActive(true))}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          className={`cursor-pointer flex flex-col items-center justify-center mx-auto transition bg-gray-50
          ${constraints.dropClass}
          ${
            dragActive
              ? "border-primary bg-primary/10 border-2"
              : "border-dashed border-gray-300 border-2 hover:border-primary"
          } rounded-lg`}
        >
          {uploading ? (
            <div className="text-gray-700">Téléchargement en cours...</div>
          ) : dragActive ? (
            <span className="text-primary font-semibold text-center">
              Déposez l’image ici
            </span>
          ) : (
            <span className="text-gray-600 flex flex-col items-center text-center px-4">
              <PhotoIcon className="w-8 h-8 text-gray-400 mb-2" />
              Cliquez ou glissez une image ici
              <div className="flex items-center mt-1 text-xs text-gray-500">
                <InformationCircleIcon
                  data-tooltip-id={tooltipId}
                  className="w-4 h-4 mr-1"
                />
                Format : {ratio === "square" ? "Carré" : "Paysage 3:2"}
              </div>
              <Tooltip
                id={tooltipId}
                place="top"
                className="z-50 max-w-xs text-xs bg-primary text-white px-3 py-2 rounded-lg"
              >
                Taille idéale : {constraints.recommended}
                <br />
                Toute taille est acceptée
                <br />
                L’image sera recadrée automatiquement
              </Tooltip>
            </span>
          )}
        </label>
      )}

      <input
        id="image-upload"
        type="file"
        accept="image/jpeg, image/png, image/webp"
        onChange={handleFileUpload}
        className="hidden"
      />

      {error && (
        <p className="text-red-500 text-sm text-center font-medium">{error}</p>
      )}
    </div>
  );
}
